// Nav-only snapshot of the parent theme's pages.json (this repo inherits its
// pages and has no src/pages.json). Keep in sync when the parent adds,
// renames, or re-parents pages.
import pagesData from './_navPages.json';

// ============================================================================
// Shared navigation eligibility
// ----------------------------------------------------------------------------
// Single source of truth for "which pages appear in the primary navigation".
// Consumed by Header (renders the nav) and SitemapContent (mirrors the nav as
// a link list), so the two never drift. See buildNavLinks / getEligibleNavPages
// below.
// ============================================================================

export interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
  /** Off-site destination — rendered with target="_blank". */
  external?: boolean;
}

/** Pages whose nav entry links straight to an external property URL
    (when the property has one) instead of the internal landing page. */
export const EXTERNAL_NAV_PAGE_PATHS = {
  applyNow: '/apply-now',
  residentPortal: '/resident-access',
} as const;

/** Swap internal hrefs for external URLs, keyed by internal page path.
    Paths with no (or a blank) external URL keep their internal href. */
export function applyExternalNavLinks(links: NavLink[], overrides: Record<string, string | null | undefined>): NavLink[] {
  return links.map((link) => {
    const url = overrides[normalizePath(link.href)]?.trim();
    const next: NavLink = url ? { ...link, href: url, external: true } : link;
    return link.children
      ? { ...next, children: applyExternalNavLinks(link.children, overrides) }
      : next;
  });
}

/** A property has a virtual tour when any `virtual_tours` row has a URL, or —
    for properties with no collection rows — the scalar
    `property_virtual_tour_url` is set. Gates the Virtual Tours page in the
    nav, sitemap, and thank-you lists. */
export function hasVirtualTourContent(
  tours: Array<{ virtual_tour_url?: string | null }> | null | undefined,
  propertyTourUrl: unknown,
): boolean {
  if ((tours || []).some((t) => !!t.virtual_tour_url?.trim())) return true;
  return typeof propertyTourUrl === 'string' && !!propertyTourUrl.trim();
}

function isFilled(value: unknown): boolean {
  return value != null && !!String(value).trim();
}

/** A property has an interactive map when it can drive either map provider
    the Interactive Map page supports: ApartmentSync (Repli360 / RRAC), which
    needs BOTH its JavaScript ID and Config ID, or Engrain SightMap, which
    needs an ID or URL (either field spelling the engrain-sitemap block reads).
    `showIfExists` is the editor override from the header's
    `interactive_map_show_if_exists` token. Gates the Interactive Map page in
    the nav, sitemap, and thank-you lists. */
export function hasInteractiveMapContent(
  property: Record<string, unknown> | null | undefined,
  showIfExists: unknown,
): boolean {
  const p = property || {};
  const hasApartmentSync =
    isFilled(p.custom_apartmentsync_javascript_id) && isFilled(p.custom_apartmentsync_config_id);
  const hasEngrain = [
    'custom_engrain_sightmap_id',
    'custom_engrain_sightmap_url',
    'custom_engrain_sitemap_id',
    'custom_engrain_sitemap_url',
  ].some((key) => isFilled(p[key]));
  return hasApartmentSync || hasEngrain || isFilled(showIfExists);
}

/** Page aliases that should never appear in the main navigation.
    Apply Now, Resident Login (resident-access), and Contact Us are
    intentionally NOT excluded — they're surfaced as primary nav links.
    Schedule a Tour is excluded from the link row because it is promoted to
    the standalone CTA button on the right. Pet Policy lives in the footer /
    sitemap rather than the top nav. */
export const NAV_EXCLUDED_ALIASES = new Set<string>([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
  'pet-policy',
]);

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
export function normalizePath(p: string): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Flat, position-sorted list of `[alias, page]` entries eligible for the
 * primary navigation. This is the canonical "pages in navigation" filter:
 * excluded aliases, hidden pages, and template pages are dropped, and the
 * Virtual Tour / Interactive Map pages appear only when the property has the
 * data to back them (see the flags passed from the caller).
 */
export function getEligibleNavPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): Array<[string, PageEntry]> {
  return (Object.entries(pagesData as Record<string, PageEntry>) as Array<[string, PageEntry]>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);
}

/**
 * Build navigation links from pages.json. Only includes eligible pages
 * (see getEligibleNavPages), sorted by position. Pages declaring
 * `parent: <alias>` are nested as `children` of their parent entry rather than
 * appearing at the top level. Orphaned children (parent missing or hidden)
 * fall back to the top level so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the Interactive
 * Map page — that page renders the ApartmentSync (Repli360 / RRAC)
 * "interactive leasing view" widget, keyed by two per-property custom fields
 * (`custom_apartmentsync_javascript_id` and `custom_apartmentsync_config_id`).
 * A property without both is a half-configured / no-real-map state, so we drop
 * the page from the nav (the SSG also marks the page hidden + noindex — see
 * ssg-entry.ts).
 */
export function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): NavLink[] {
  const eligible = getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing);
  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}
